/**
 * Single source of truth for resolving a suggestion's content references to
 * human-readable titles (and best-effort content types), keyed by the content
 * id as a string.
 *
 * Resolution sources, merged lowest-to-highest priority so the resolved title
 * stays consistent with what the card summary shows (`SuggestionSummary` via
 * `parseSuggestionTitle`):
 *
 * 1. `resolvedContent` — externally resolved details (e.g. the AGG content lookup
 *    performed by `useFetchSpaceSuggestions`). Fallback for ids not mentioned in
 *    the title.
 * 2. `titleReferences` — legacy structured id→{title,contentType} map. Kept as a
 *    fallback source. Keyed here by BOTH the record key and the value's
 *    `contentId` so either backend keying resolves.
 * 3. `contentReferences` — the preferred, authoritative id→{title,contentType}
 *    map from the backend. Overrides `titleReferences`/`resolvedContent`. Indexed
 *    the same way as `titleReferences`.
 * 4. Inline title link-segments — the `{contentId:…,title:…}` object literals and
 *    resolved bare `{id}` refs parsed out of the suggestion title. These encode
 *    the exact per-reference choice the card renders, so they win. New payloads
 *    carry plain-text titles (no segments), so they only apply to legacy titles.
 *
 * Content ids are numeric. The map is keyed by the id's string form — inherent
 * because JS object keys are strings and the resolution sources (parsed title
 * segments, the reference maps, and the AGG lookup) all supply string ids — while
 * numeric lookups coerce to the same key automatically.
 */

import type {
	ContentDetailsById,
	ContentReferences,
	RawAction,
	ResolvedContent,
	TitleReferences,
} from '../__types__/SpaceSuggestion.ts';
import { parseSuggestionTitle } from './parseSuggestionTitle.ts';

export type ContentDetailsSources = {
	/** Preferred, authoritative references keyed by content id. */
	contentReferences?: ContentReferences;
	/** Legacy references, kept as a fallback source. */
	titleReferences?: TitleReferences;
	/** AGG-resolved seed, the lowest-priority fallback. */
	resolvedContent?: ContentDetailsById;
};

/**
 * Merges a reference map into `details`, indexing each entry by both the record
 * key and the value's `contentId` so the action's contentId resolves regardless
 * of which the backend keys by. Entries without a title are skipped.
 */
function mergeReferences(details: ContentDetailsById, refs?: TitleReferences): void {
	if (!refs) {
		return;
	}
	// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
	for (const key in refs) {
		const ref = refs[key];
		if (!ref || !ref.title) {
			continue;
		}
		const resolved: ResolvedContent = {
			title: ref.title,
			contentType: ref.contentType || undefined,
		};
		details[key] = resolved;
		if (ref.contentId) {
			details[ref.contentId] = resolved;
		}
	}
}

export function buildContentDetails(
	title: string,
	sources: ContentDetailsSources = {},
): ContentDetailsById {
	const { contentReferences, titleReferences, resolvedContent } = sources;
	const details: ContentDetailsById = { ...resolvedContent };

	// titleReferences first, then contentReferences so the preferred map wins.
	mergeReferences(details, titleReferences);
	mergeReferences(details, contentReferences);

	// Prefer the preferred references when parsing bare `{id}` refs in the title,
	// falling back to the legacy map.
	for (const segment of parseSuggestionTitle(title, contentReferences ?? titleReferences)) {
		if (segment.type === 'link') {
			details[segment.contentId] = {
				title: segment.title,
				contentType: segment.contentType,
			};
		}
	}

	return details;
}

/**
 * Resolves a content id to its display title, falling back to the bare id as a
 * last resort so a missing resolution degrades to the id rather than an empty
 * string.
 */
export function resolveContentName(
	contentId: number,
	details: ContentDetailsById,
): string | number {
	return details[contentId]?.title ?? contentId;
}

/**
 * Resolves the content type used to pick a row/link icon, or `undefined` when
 * unresolved so callers can apply their own fallback icon.
 */
export function resolveContentType(
	contentId: number,
	details: ContentDetailsById,
): string | undefined {
	return details[contentId]?.contentType;
}

/**
 * Builds a map from a CREATE_FOLDER `alias` to its folder title within a single
 * suggestion. Used to resolve a MOVE action's `destinationAlias` to a
 * human-readable folder name created earlier in the same suggestion.
 */
export function buildAliasToTitleMap(actions: RawAction[]): Record<string, string> {
	const map: Record<string, string> = {};
	for (const action of actions) {
		if (action.actionType === 'CREATE_FOLDER') {
			map[action.parameters.alias] = action.parameters.title;
		}
	}
	return map;
}
